import { yupResolver } from '@hookform/resolvers/yup';
import InformationCircleIcon from '@heroicons/react/24/outline/InformationCircleIcon';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import InputCheckbox from '../InputCheckbox';
import InputField from '../InputField';
import PhoneNumber from '../PhoneNumber';
import SessionsFormField from './SessionsFormField';
import useSessionsNewAnonymousParticipantSchema from './useSessionsNewAnonymousParticipantSchema';
import SMSFeatureNotice from '../SMSFeatureNotice/SMSFeatureNotice';
import Link from '../Link';

const SessionsNewAnonymousParticipantForm = ({
  onSubmit,
  maxParticipants,
  onDeleteParticipant,
  referenceType,
  defaultParticipant,
  hideParticipantInvitationOption,
  canSendSms,
  smsUpgradeUrl,
}) => {
  const validationSchema = useSessionsNewAnonymousParticipantSchema(referenceType);
  const {
    register,
    handleSubmit: wrapSubmit,
    reset,
    errors,
    watch,
    control,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });
  const { t } = useTranslation('components', {
    keyPrefix: 'Sessions.SessionsNewAnonymousParticipantForm',
  });

  const handleSubmit = ({ firstName, lastName, ...data }) => {
    onSubmit({
      firstName,
      lastName,
      referenceType,
      reference: data[referenceType],
    });
    reset();
  };

  const values = watch();

  const validateAndSubmit = async data => {
    try {
      await validationSchema.validate(data);
      onSubmit({
        firstName: data.firstName,
        lastName: data.lastName,
        referenceType: data.referenceType,
        reference: data[referenceType],
        inviteParticipant: data.inviteParticipant,
      });
    } catch {
      if (Object.keys(data).length) onDeleteParticipant();
    }
  };

  useEffect(() => {
    if (maxParticipants === 2 && Object.keys(values).length) {
      validateAndSubmit(values);
    }
  }, [values, maxParticipants]);

  if (!canSendSms && referenceType === 'phone') {
    return (
      <SMSFeatureNotice title={t('smsNotice.title')} description={t('smsNotice.description')}>
        <Link
          href={smsUpgradeUrl}
          target="_blank"
          rel="noreferrer"
          kind="button"
          className="text-lg"
        >
          {t('smsNotice.cta')}
        </Link>
      </SMSFeatureNotice>
    );
  }

  return (
    <form className="flex flex-wrap" onSubmit={wrapSubmit(handleSubmit)}>
      <input type="hidden" ref={register} name="referenceType" value={referenceType} />
      <SessionsFormField className="flex-grow mb-4" label={t('firstName')} error={errors.firstName}>
        <InputField
          name="firstName"
          inputClassName="w-full"
          ref={register}
          defaultValue={defaultParticipant ? defaultParticipant.firstName : ''}
        />
      </SessionsFormField>
      <div className="h-0 w-full lg:w-4" />
      <SessionsFormField className="flex-grow mb-4" label={t('lastName')} error={errors.lastName}>
        <InputField
          name="lastName"
          inputClassName="w-full"
          ref={register}
          defaultValue={defaultParticipant ? defaultParticipant.lastName : ''}
        />
      </SessionsFormField>
      <div className="w-full mb-8" hidden={referenceType !== 'email'}>
        <SessionsFormField className="mb-1" label={t('email')} error={errors.email}>
          <InputField
            type="email"
            name="email"
            ref={register}
            defaultValue={
              (defaultParticipant &&
                defaultParticipant.referenceType === 'email' &&
                defaultParticipant.reference) ||
              ''
            }
          />
        </SessionsFormField>
      </div>
      <div className="w-full mb-8" hidden={referenceType !== 'phone'}>
        <SessionsFormField
          label={<span>{t('phone.title')}</span>}
          className="mb-1"
          error={errors.phone}
        >
          <PhoneNumber
            name="phone"
            control={control}
            defaultValue={
              (defaultParticipant &&
                defaultParticipant.referenceType === 'phone' &&
                defaultParticipant.reference) ||
              ''
            }
            placeholder={t('phone.placeholder')}
          />
        </SessionsFormField>
      </div>
      {!hideParticipantInvitationOption && maxParticipants === 2 && (
        <div className="mb-8">
          <h3 className="flex font-semibold mb-4 text-slate-600">
            <span>{t('inviteParticipant.headline')}</span>{' '}
            <a
              href="https://medflex.de/hilfe/behandler-video/videotermin-anlegen/#0-toc-title"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 ml-1"
            >
              <InformationCircleIcon className="h-6 w-6" />
            </a>
          </h3>
          <label className="flex cursor-pointer" htmlFor="inviteParticipant">
            <InputCheckbox
              id="inviteParticipant"
              ref={register}
              name="inviteParticipant"
              defaultChecked={false}
            />
            <span className="ml-2">{t('inviteParticipant.label')}</span>
          </label>
        </div>
      )}
      {maxParticipants > 2 && (
        <Button className="w-full" type="submit">
          {t('submit')}
        </Button>
      )}
    </form>
  );
};

SessionsNewAnonymousParticipantForm.propTypes = {
  onSubmit: PropTypes.func,
  hideParticipantInvitationOption: PropTypes.bool,
  maxParticipants: PropTypes.number,
  onDeleteParticipant: PropTypes.func,
  referenceType: PropTypes.oneOf(['email', 'phone']),
  defaultParticipant: PropTypes.shape({
    userId: PropTypes.string,
    fullNameWithTitle: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    avatar: PropTypes.string,
    referenceType: PropTypes.string,
    initials: PropTypes.string,
    reference: PropTypes.string,
  }),
  canSendSms: PropTypes.bool.isRequired,
  smsUpgradeUrl: PropTypes.string,
};

SessionsNewAnonymousParticipantForm.defaultProps = {
  onSubmit: () => {},
  onDeleteParticipant: () => {},
  maxParticipants: 9,
  hideParticipantInvitationOption: false,
  referenceType: 'email',
  smsUpgradeUrl: '',
};

export default SessionsNewAnonymousParticipantForm;
